/** QuickRouter — Contrôle des règles de conception (DRC).
 *
 * Erreurs : isolement cuivre-cuivre entre nets différents, cuivre trop près du bord,
 * composant hors carte, cuivre dans une zone interdite (antenne), perçage trop petit,
 * liaisons non routées. Avertissements : chevauchement d'encombrements, net à une
 * seule broche, piste plus fine que ce que fabriquent les services courants.
 *
 * Multicouche : chaque couche est contrôlée à part (traversants et vias sur toutes) ; piste
 * sur une couche absente → erreur ; piste sur une couche « plan » → avertissement. Domaines
 * isolés (net.domain) : rules.isolation entre domaines différents (ou domaine / reste).
 * Paires différentielles : largeur et écart attendus (classe / impédance), différence de
 * longueur intra-paire (rules.pairSkew ou celle de la classe).
 */
import {
  padShape, segShape, discShape, shapeDist, shapeBox, boxesNear, shapeEdgeDist,
  courtyardPoly, pointInPoly, placePoint, r4, obstaclePoly, polyBox,
} from './geom.js';
import { allPads, padNetMap, routingStatus } from './netlist.js';
import { copperLayers, layerRole } from './layers.js';
import { pairReport } from './diffpair.js';

const EPS = 0.002;
const FAB_MIN_TRACK = { 2: 0.127, 4: 0.09, 6: 0.09 }; // minima JLCPCB courants (mm)
const MAX_ISSUES = 200;

const mid = (s) => (s.kind === 'cap' ? [(s.a[0] + s.b[0]) / 2, (s.a[1] + s.b[1]) / 2] : s.pts[0]);
const fmt = (v) => (Math.round(v * 1000) / 1000).toLocaleString('fr-FR');

/** Cuivre de la carte : [{ label, net, layers, shape, box, owner }] */
function copper(project, lookup) {
  const nets = padNetMap(project);
  const out = [];
  for (const p of allPads(project, lookup)) {
    if (!p.layers.length) continue;
    // Pastille sans net : équipotentielle à elle seule (identifiée par sa clé).
    out.push({ kind: 'pad', label: `pastille ${p.key}${p.name ? ` (${p.name})` : ''}`, net: nets.get(p.key) ?? `#${p.key}`, layers: p.layers, shape: padShape(p), owner: p.key, comp: p.ref });
  }
  for (const t of project.tracks) {
    for (let i = 1; i < t.pts.length; i++) {
      out.push({ kind: 'track', label: `piste ${t.net || '(sans net)'} ${t.layer}`, net: t.net || `#${t.id}`, layers: [t.layer], shape: segShape(t.pts[i - 1], t.pts[i], t.width), owner: t.id });
    }
  }
  for (const v of project.vias) {
    out.push({ kind: 'via', label: `via ${v.net || '(sans net)'}`, net: v.net || `#${v.id}`, layers: copperLayers(project.board), shape: discShape(v.pos, v.diameter), owner: v.id });
  }
  for (const c of out) c.box = shapeBox(c.shape);
  return out;
}

export function runDrc(project, lookup) {
  const rules = project.rules;
  const errors = [];
  const warnings = [];
  const push = (list, type, message, at, extra = {}) => { if (list.length < MAX_ISSUES) list.push({ type, message, at: at ? at.map(r4) : null, ...extra }); };

  const cu = copper(project, lookup);
  const domainOf = new Map(project.nets.filter((n) => n.domain).map((n) => [n.name, n.domain]));
  const anyDomain = domainOf.size > 0 && rules.isolation > 0;
  const required = (a, b) => {
    if (!anyDomain) return rules.clearance;
    const da = domainOf.get(a.net) ?? '', db = domainOf.get(b.net) ?? '';
    return (da || db) && da !== db ? Math.max(rules.isolation, rules.clearance) : rules.clearance;
  };
  const reach = anyDomain ? Math.max(rules.isolation, rules.clearance) : rules.clearance;

  // 1. Isolement entre nets différents, couche par couche (isolation entre domaines isolés).
  for (let i = 0; i < cu.length; i++) {
    const a = cu[i];
    for (let j = i + 1; j < cu.length; j++) {
      const b = cu[j];
      if (a.net === b.net) continue;
      if (!a.layers.some((l) => b.layers.includes(l))) continue;
      if (!boxesNear(a.box, b.box, reach)) continue;
      const need = required(a, b);
      const d = shapeDist(a.shape, b.shape);
      if (d < need - EPS) {
        const iso = need > rules.clearance;
        push(errors, d <= 0 ? 'short' : iso ? 'isolation' : 'clearance',
          d <= 0
            ? `Court-circuit : ${a.label} touche ${b.label} (nets ${a.net} / ${b.net})`
            : iso
              ? `Isolement entre domaines ${fmt(d)} mm < ${fmt(need)} mm : ${a.label} (${domainOf.get(a.net) ?? 'non isolé'}) et ${b.label} (${domainOf.get(b.net) ?? 'non isolé'})`
              : `Isolement ${fmt(d)} mm < ${fmt(rules.clearance)} mm entre ${a.label} et ${b.label}`,
          mid(a.shape), { items: [a.owner, b.owner] });
      }
    }
  }

  // 1 bis. Couches : piste sur une couche absente de la carte, ou sur un plan.
  const layers = copperLayers(project.board);
  for (const t of project.tracks) {
    if (!layers.includes(t.layer)) push(errors, 'layer', `Piste ${t.net} sur la couche ${t.layer}, absente d'une carte ${project.board.layers} couches`, t.pts[0], { items: [t.id] });
    else if (layerRole(project.board, t.layer) === 'plane') push(warnings, 'plane-track', `Piste ${t.net} sur ${t.layer}, couche de plan : elle coupe le plan de référence`, t.pts[0], { items: [t.id] });
  }
  for (const z of project.zones) {
    if (!layers.includes(z.layer)) push(errors, 'layer', `Zone ${z.net} sur la couche ${z.layer}, absente de la carte`, null, { items: [z.id] });
  }

  // 1 ter. Paires différentielles.
  for (const r of pairReport(project, lookup)) {
    if (!r.partnerFound) { push(warnings, 'pair', `Paire ${r.p} / ${r.n} : net partenaire introuvable`, null); continue; }
    if (r.lengthP > 0 && r.lengthN > 0 && Math.abs(r.lengthP - r.lengthN) > r.maxSkew + EPS) {
      push(warnings, 'pair-skew', `Paire ${r.p} / ${r.n} : ${fmt(r.lengthP)} mm contre ${fmt(r.lengthN)} mm (écart ${fmt(Math.abs(r.lengthP - r.lengthN))} > ${fmt(r.maxSkew)} mm)`, null, { net: r.p });
    }
    for (const g of r.gapIssues) push(warnings, 'pair-gap', `Paire ${r.p} / ${r.n} sur ${g.layer} : écart ${fmt(g.gap)} mm au lieu de ${fmt(g.expected)} mm (impédance non tenue)`, g.at, { net: r.p });
    for (const w of r.widthIssues) push(warnings, 'pair-width', `Paire ${r.p} / ${r.n} sur ${w.layer} : piste de ${fmt(w.width)} mm au lieu de ${fmt(w.expected)} mm (impédance ${r.impedance} Ω)`, w.at, { net: r.p });
  }

  // 2. Bord de carte.
  const outline = project.board.outline;
  for (const c of cu) {
    const d = shapeEdgeDist(c.shape, outline);
    if (d < rules.edgeClearance - EPS) {
      push(errors, 'edge', d < 0 ? `${c.label} déborde du contour de la carte` : `${c.label} à ${fmt(d)} mm du bord (mini ${fmt(rules.edgeClearance)} mm)`, mid(c.shape), { items: [c.owner] });
    }
  }

  // 3. Composants : hors carte, chevauchements, zones interdites.
  const courts = [];
  for (const comp of project.components) {
    const fp = lookup(comp.footprint);
    if (!fp) { push(errors, 'footprint', `${comp.ref} : empreinte ${comp.footprint} introuvable`, comp.pos, { items: [comp.ref] }); continue; }
    const poly = courtyardPoly(comp, fp);
    if (poly) {
      if (poly.some((p) => !pointInPoly(p, outline))) push(errors, 'outside', `${comp.ref} sort du contour de la carte`, comp.pos, { items: [comp.ref] });
      courts.push({ comp, poly, box: shapeBox({ kind: 'poly', pts: poly }), tht: fp.pads.some((p) => p.drill) });
    }
    for (const ko of fp.keepouts ?? []) {
      const kpoly = [[ko.min[0], ko.min[1]], [ko.max[0], ko.min[1]], [ko.max[0], ko.max[1]], [ko.min[0], ko.max[1]]].map((p) => placePoint(comp, p));
      const kshape = { kind: 'poly', pts: kpoly };
      const kbox = shapeBox(kshape);
      const side = comp.side === 'bottom' ? ko.layers.map((l) => (l === 'F' ? 'B' : 'F')) : ko.layers;
      const layers = side.includes('F') && side.includes('B') ? copperLayers(project.board) : side;
      for (const c of cu) {
        if (c.comp === comp.ref) continue;
        if (!c.layers.some((l) => layers.includes(l)) || !boxesNear(c.box, kbox)) continue;
        if (shapeDist(c.shape, kshape) <= 0) push(errors, 'keepout', `${c.label} dans la zone interdite « ${ko.label || 'keepout'} » de ${comp.ref}`, mid(c.shape), { items: [c.owner, comp.ref] });
      }
      for (const z of project.zones) {
        if (!layers.includes(z.layer) || !overlapsStrict(z.outline ?? outline, kpoly)) continue;
        push(warnings, 'keepout-zone', `La zone ${z.net} (${z.layer}) recouvre la zone interdite « ${ko.label || 'keepout'} » de ${comp.ref} : le cuivre y est retiré automatiquement à la fabrication`, kpoly[0]);
      }
    }
  }
  for (let i = 0; i < courts.length; i++) {
    for (let j = i + 1; j < courts.length; j++) {
      const a = courts[i], b = courts[j];
      // Un CMS dessous peut passer sous un CMS dessus ; un traversant occupe les deux faces.
      if (a.comp.side !== b.comp.side && !a.tht && !b.tht) continue;
      if (a.comp.locked && b.comp.locked) continue; // géométrie imposée par la carte mère
      if (!boxesNear(a.box, b.box)) continue;
      if (overlapsStrict(a.poly, b.poly)) {
        push(warnings, 'overlap', `Encombrements de ${a.comp.ref} et ${b.comp.ref} se chevauchent`, a.comp.pos, { items: [a.comp.ref, b.comp.ref] });
      }
    }
  }

  // Carte mère : la carte et ce qui dépasse de sa face tournée vers la mère doivent passer
  // les obstacles (hauteur libre = gap) : corps d'un composant posé sur cette face, ou
  // ~1,5 mm de pattes d'un traversant posé sur l'autre.
  const mother = project.board.mother;
  if (mother) {
    const obs = mother.obstacles.map((o) => ({ o, poly: obstaclePoly(o) }));
    for (const { o, poly } of obs) {
      if (o.height >= mother.gap - EPS && overlapsStrict(outline, poly)) {
        const b = polyBox(poly);
        push(errors, 'mother', `La carte fille heurte « ${o.label} » de la ${mother.name} : ${fmt(o.height)} mm de haut pour ${fmt(mother.gap)} mm de hauteur libre`, [(b[0] + b[2]) / 2, (b[1] + b[3]) / 2]);
      }
    }
    for (const { comp, poly, tht } of courts) {
      if (comp.locked) continue;
      // Face de la carte tournée vers la mère : dessous pour un shield, dessus pour une porteuse.
      const toward = mother.side === 'above' ? 'top' : 'bottom';
      const under = comp.side === toward ? (lookup(comp.footprint)?.body?.size[2] ?? 1) : tht ? 1.5 : 0;
      if (!under) continue;
      for (const { o, poly: op } of obs) {
        if (o.height + under > mother.gap - EPS && overlapsStrict(poly, op)) {
          push(warnings, 'mother-clash', `${comp.ref} dépasse de ${fmt(under)} mm vers la ${mother.name}, face à « ${o.label} » (${fmt(o.height)} mm) : ${fmt(mother.gap)} mm seulement entre les cartes`, comp.pos, { items: [comp.ref] });
        }
      }
    }
  }

  // 4. Perçages et largeurs.
  for (const v of project.vias) {
    if (v.drill < rules.minDrill - EPS) push(errors, 'drill', `Via ${v.net} : perçage ${fmt(v.drill)} < ${fmt(rules.minDrill)} mm`, v.pos, { items: [v.id] });
    if (v.diameter - v.drill < 0.2 - EPS) push(warnings, 'annular', `Via ${v.net} : couronne de ${fmt((v.diameter - v.drill) / 2)} mm (0,1 mm mini conseillé)`, v.pos, { items: [v.id] });
  }
  const fabMin = FAB_MIN_TRACK[project.board.layers] ?? 0.127;
  for (const t of project.tracks) {
    if (t.width < fabMin - EPS) push(warnings, 'width', `Piste ${t.net} : ${fmt(t.width)} mm, plus fine que le minimum courant de fabrication (${fmt(fabMin)} mm en ${project.board.layers} couches)`, t.pts[0], { items: [t.id] });
  }

  // 5. Connexions.
  const status = routingStatus(project, lookup);
  for (const n of status.nets) {
    if (n.pads < 2) push(warnings, 'single', `Net ${n.name} : une seule broche`, null);
    for (const r of n.rats) push(errors, 'unrouted', `Net ${n.name} : liaison non routée ${r.from ?? ''} → ${r.to} (${fmt(r.length)} mm)`, r.a, { net: n.name });
  }

  return {
    ok: errors.length === 0,
    errors,
    warnings,
    summary: {
      errors: errors.length,
      warnings: warnings.length,
      unrouted: status.unrouted,
      nets: status.total,
      routedNets: status.routed,
    },
  };
}

/** Recouvrement d'aires (et non simple contact) : un sommet de l'un strictement dans l'autre. */
function overlapsStrict(p, q) {
  const shrink = (poly) => {
    const cx = poly.reduce((s, x) => s + x[0], 0) / poly.length, cy = poly.reduce((s, x) => s + x[1], 0) / poly.length;
    return poly.map(([x, y]) => [cx + (x - cx) * 0.98, cy + (y - cy) * 0.98]);
  };
  const ps = shrink(p), qs = shrink(q);
  return shapeDist({ kind: 'poly', pts: ps }, { kind: 'poly', pts: qs }) <= 0;
}
